import { useCallback } from "react";
import { useAiActionStore, AI_AGENT } from "@/store/zustand/aiActionStore";
import { useSupabaseStore } from "@/store/zustand/supabaseStore";
import { useCanvasId } from "@/utils/utils";
import { v4 as uuidv4 } from "uuid";

/**
 * Hook for handling conversation switching based on model selection.
 *
 * For PM Agent (projects page): Simple - just get/create conversations for the canvas.
 * For Editor (canvas page): Handles switching between Claude Agent and Max Agent conversations.
 */
export const useConversationSwitching = () => {
  const canvasId = useCanvasId();

  const switchToModelConversation = useCallback(async () => {
    if (!canvasId) {
      console.warn("Cannot switch conversation: no canvas ID available");
      return;
    }

    const { supabaseClient, aiChatConversations } = useSupabaseStore.getState();
    const currentAgent = useAiActionStore.getState().agent;

    // PM Agent doesn't distinguish between conversation types - just use any conversation
    const isPMAgent = currentAgent === AI_AGENT.PM_AGENT;
    const isClaudeAgent = currentAgent === AI_AGENT.CLAUDE_AGENT;

    try {
      // Filter conversations for this canvas
      const allCanvasConversations = Object.values(aiChatConversations)
        .filter((c) => c.canvas_id === canvasId)
        .sort(
          (a, b) =>
            new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
        );

      // For PM Agent: use any conversation. For others: filter by type.
      const conversations = isPMAgent
        ? allCanvasConversations
        : allCanvasConversations.filter((c) => {
            const hasSessionId = !!c.ai_provider_session_id;
            return isClaudeAgent ? hasSessionId : !hasSessionId;
          });

      if (conversations.length > 0) {
        useAiActionStore
          .getState()
          .setSelectionConversationId(conversations[0].id);
        return;
      }

      // Store might not be loaded yet - check database directly
      if (supabaseClient) {
        const { data: existingConversations } = await supabaseClient
          .from("ai_chat_conversations")
          .select("*")
          .eq("canvas_id", canvasId)
          .order("created_at", { ascending: false });

        if (existingConversations?.length) {
          // For PM Agent: use any. For others: filter by type.
          const matchingConversation = isPMAgent
            ? existingConversations[0]
            : existingConversations.find((c) => {
                const hasSessionId = !!c.ai_provider_session_id;
                return isClaudeAgent ? hasSessionId : !hasSessionId;
              });

          if (matchingConversation) {
            useSupabaseStore.getState().onDbSnapshot("aiChatConversations", {
              newItems: existingConversations,
              deletedItems: [],
            });
            useAiActionStore
              .getState()
              .setSelectionConversationId(matchingConversation.id);
            return;
          }
        }
      }

      // No existing conversations, create a new one
      const conversationId = uuidv4();
      const conversation = {
        id: conversationId,
        canvas_id: canvasId,
        created_at: new Date().toISOString(),
        // Only Claude Agent gets ai_provider_session_id
        ...(isClaudeAgent && { ai_provider_session_id: "claude_code" }),
      };

      if (!supabaseClient) {
        throw new Error("Supabase client not available");
      }

      const { error: insertError } = await supabaseClient
        .from("ai_chat_conversations")
        .insert([conversation]);

      if (insertError) {
        console.error("Failed to insert conversation:", insertError);
        throw insertError;
      }

      useSupabaseStore.getState().onDbSnapshot("aiChatConversations", {
        newItems: [conversation],
        deletedItems: [],
      });

      useAiActionStore.getState().setSelectionConversationId(conversationId);
    } catch (error) {
      console.error("Failed to switch conversation:", error);
      throw error;
    }
  }, [canvasId]);

  return {
    switchToModelConversation,
  };
};
